import type { LucideIcon } from "lucide-react";
import type * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const ENTITY_CARD_GRID = "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3";

// Stable keys for the loading placeholders — avoids array-index keys.
const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"] as const;

/**
 * Card chrome shared by every directory's card view. Pass `interactive: true`
 * for cards that are links (adds hover/focus affordances); `false` for static
 * cards whose actions live inside them (e.g. the users menu).
 */
export function entityCardClass(interactive: boolean): string {
  return cn(
    "flex items-center gap-4 rounded-xl border bg-card p-4 shadow-sm transition-colors",
    interactive &&
      "hover:border-ring/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  );
}

/** Tinted square holding the entity's icon — the card's stand-in for an avatar. */
function EntityIconTile({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary",
        className,
      )}
    >
      <Icon className="size-6" />
    </span>
  );
}

/**
 * The card's leading visual: either the standard tinted icon tile (`icon`) or a
 * custom node such as an avatar (`leading`). Exactly one of the two.
 */
type EntityCardLeading =
  | { icon: LucideIcon; iconClassName?: string; leading?: never }
  | { leading: React.ReactNode; icon?: never; iconClassName?: never };

interface EntityCardBaseProps {
  title: string;
  /** Secondary line under the title. Rendered muted; omit for no second line. */
  subtitle?: React.ReactNode;
  /** Extra content below the subtitle, e.g. a row of badges. */
  footer?: React.ReactNode;
  /** Right-aligned content, e.g. a status badge or an actions menu. */
  trailing?: React.ReactNode;
}

type EntityCardContentProps = EntityCardBaseProps & EntityCardLeading;

/**
 * The inner layout for an entity card: the leading visual on the left, title +
 * subtitle (and optional footer) in the middle, optional trailing slot on the
 * right.
 * Render it inside a `<Link>` (interactive) or `<div>` (static) that carries
 * `entityCardClass(...)`.
 */
export function EntityCardContent({
  icon,
  iconClassName,
  leading,
  title,
  subtitle,
  footer,
  trailing,
}: EntityCardContentProps) {
  return (
    <>
      {leading ?? (icon ? <EntityIconTile icon={icon} className={iconClassName} /> : null)}
      <div className="min-w-0 flex-1 leading-tight">
        <div className="break-words font-medium text-foreground">{title}</div>
        {subtitle !== undefined && (
          <div className="break-words text-sm text-muted-foreground">{subtitle}</div>
        )}
        {footer && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{footer}</div>}
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </>
  );
}

interface EntityCardsGridProps {
  loading?: boolean;
  isEmpty: boolean;
  /** Shown when there are no items — usually the same `<EmptyState>` as the table. */
  empty: React.ReactNode;
  children: React.ReactNode;
}

/** Grid wrapper owning the responsive layout, loading skeletons, and empty state. */
export function EntityCardsGrid({ loading, isEmpty, empty, children }: EntityCardsGridProps) {
  if (loading) {
    return (
      <div className={ENTITY_CARD_GRID}>
        {SKELETON_KEYS.map((key) => (
          <div
            key={key}
            className="flex items-center gap-4 rounded-xl border bg-card p-4 shadow-sm"
          >
            <Skeleton className="size-14 shrink-0 rounded-xl" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isEmpty) return <>{empty}</>;

  return <div className={ENTITY_CARD_GRID}>{children}</div>;
}
